<template>
  <div>
    <Navigation />
    <div class="kds">
      <div class="kds-head">
        <div>
          <h1>Kitchen Display</h1>
          <p class="lede">Tap tickets to advance them: New → Preparing → Ready → Served. Auto-refreshes every 5 seconds.</p>
        </div>
        <div class="filters">
          <label class="inline">
            <span>Station:</span>
            <select v-model.number="stationFilter" @change="load">
              <option :value="0">All stations</option>
              <option v-for="s in stations" :key="s.id" :value="s.id">{{ s.name }} ({{ s.type }})</option>
            </select>
          </label>
          <button class="secondary" @click="load">Refresh now</button>
        </div>
      </div>

      <div class="board">
        <div class="col" v-for="col in columns" :key="col.status">
          <div class="col-head" :class="'col-' + col.status">
            <span>{{ col.label }}</span>
            <span class="count">{{ ticketsByStatus[col.status].length }}</span>
          </div>
          <div class="col-body">
            <div v-for="t in ticketsByStatus[col.status]" :key="t.id" class="ticket" :class="{ overdue: isOverdue(t) }">
              <div class="ticket-head">
                <span class="table">{{ t.table_name || 'TA' }}</span>
                <span class="time">{{ elapsed(t) }}</span>
              </div>
              <div class="ticket-item">
                <strong>{{ Math.round(t.quantity) }}×</strong> {{ t.item_name }}
              </div>
              <div v-if="t.notes" class="ticket-note">{{ t.notes }}</div>
              <div class="ticket-station muted small">{{ t.station_name }}</div>
              <div class="ticket-actions">
                <button v-if="col.next" :class="col.nextClass" @click="advance(t, col.next)">
                  <i class="mdi" :class="col.nextIcon"></i>
                  {{ col.nextLabel }}
                </button>
                <button v-if="t.status === 'new'" class="secondary sm" @click="advance(t, 'cancelled')">
                  <i class="mdi mdi-close"></i>
                </button>
              </div>
            </div>
            <p v-if="ticketsByStatus[col.status].length === 0" class="muted small center">nothing here</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from '@/components/paysol-nav/Navigation.vue';
import { paysol, execute } from '@/api';

const COLS = [
  { status: 'new',       label: 'NEW',       next: 'preparing', nextLabel: 'Start',  nextClass: 'primary', nextIcon: 'mdi-fire' },
  { status: 'preparing', label: 'PREPARING', next: 'ready',     nextLabel: 'Ready',  nextClass: 'primary', nextIcon: 'mdi-bell-ring' },
  { status: 'ready',     label: 'READY',     next: 'served',    nextLabel: 'Served', nextClass: 'secondary', nextIcon: 'mdi-check' },
];

export default {
  name: 'KitchenDisplay',
  components: { Navigation },
  data() {
    return {
      tickets: [], stations: [], stationFilter: 0,
      columns: COLS,
      pollHandle: null,
      now: Date.now(),
    };
  },
  computed: {
    ticketsByStatus() {
      const out = { new: [], preparing: [], ready: [] };
      this.tickets.forEach(t => { if (out[t.status]) out[t.status].push(t); });
      return out;
    }
  },
  async mounted() {
    await Promise.all([this.load(), this.loadStations()]);
    this.pollHandle = setInterval(() => { this.now = Date.now(); this.load(); }, 5000);
  },
  beforeUnmount() { if (this.pollHandle) clearInterval(this.pollHandle); },
  methods: {
    async load() {
      const { data } = await execute({ function: 'listKitchenTickets', stationId: this.stationFilter }, paysol);
      if (data.success) this.tickets = data.data;
    },
    async loadStations() {
      const { data } = await execute({ function: 'listStations' }, paysol);
      if (data.success) this.stations = data.data;
    },
    elapsed(t) {
      const created = new Date(t.created_at).getTime();
      if (!created) return '';
      const secs = Math.max(0, Math.floor((this.now - created) / 1000));
      const m = Math.floor(secs / 60);
      const s = secs % 60;
      return `${m}:${s.toString().padStart(2, '0')}`;
    },
    isOverdue(t) {
      if (!t.prep_time_target_minutes) return false;
      const created = new Date(t.created_at).getTime();
      const targetMs = Number(t.prep_time_target_minutes) * 60 * 1000;
      return this.now - created > targetMs;
    },
    async advance(t, newStatus) {
      const { data } = await execute({ function: 'updateTicketStatus', id: t.id, status: newStatus }, paysol);
      if (data.success) await this.load();
      else alert(data.message);
    },
  }
};
</script>

<style scoped>
.kds { padding: 16px 24px 24px; }
.kds-head { display: flex; justify-content: space-between; align-items: end; margin-bottom: 20px; }
h1 { font-size: 26px; margin: 0 0 4px; color: var(--paysol-text); }
.lede { color: var(--paysol-muted); margin: 0; font-size: 13px; }
.filters { display: flex; gap: 12px; align-items: end; }
.inline { display: inline-flex; flex-direction: column; gap: 4px; margin: 0; text-transform: none; letter-spacing: 0; font-weight: 500; font-size: 11px; color: var(--paysol-muted); }
.inline select { min-width: 160px; }
.board { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; min-height: calc(100vh - 200px); }
.col { display: flex; flex-direction: column; background: var(--paysol-bg-elev); border-radius: 8px; overflow: hidden; }
.col-head { padding: 10px 14px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; font-size: 12px; display: flex; justify-content: space-between; }
.col-head.col-new       { background: rgba(139,164,196,.2); color: var(--paysol-muted); }
.col-head.col-preparing { background: rgba(245,158,11,.2); color: var(--paysol-warning); }
.col-head.col-ready     { background: rgba(16,185,129,.2); color: var(--paysol-success); }
.count { background: rgba(255,255,255,.1); padding: 1px 8px; border-radius: 10px; font-size: 11px; }
.col-body { flex: 1; padding: 10px; display: flex; flex-direction: column; gap: 10px; overflow-y: auto; }
.ticket {
  background: var(--paysol-card); border: 1px solid var(--paysol-line);
  border-radius: 8px; padding: 12px; display: flex; flex-direction: column; gap: 6px;
}
.ticket.overdue { border-color: var(--paysol-danger); background: rgba(239,68,68,.05); animation: pulse 1.5s infinite; }
@keyframes pulse { 0%,100% { border-color: var(--paysol-danger); } 50% { border-color: rgba(239,68,68,.4); } }
.ticket-head { display: flex; justify-content: space-between; }
.table { background: var(--paysol-navy-deep); color: var(--paysol-teal); padding: 3px 8px; border-radius: 4px; font-weight: 700; font-size: 12px; }
.time { color: var(--paysol-muted); font-family: 'JetBrains Mono', monospace; font-size: 12px; }
.ticket-item { color: var(--paysol-text); font-size: 15px; }
.ticket-item strong { color: var(--paysol-teal); font-size: 18px; }
.ticket-note { background: rgba(245,158,11,.1); border-left: 2px solid var(--paysol-warning); padding: 4px 8px; font-size: 12px; color: var(--paysol-warning); border-radius: 3px; }
.ticket-station { text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600; }
.ticket-actions { display: flex; gap: 6px; margin-top: 4px; }
.ticket-actions button { flex: 1; padding: 8px; font-size: 13px; display: flex; align-items: center; justify-content: center; gap: 4px; }
.ticket-actions button.sm { flex: 0 0 auto; padding: 8px 10px; }
.muted { color: var(--paysol-muted); }
.small { font-size: 11px; }
.center { text-align: center; padding: 30px 0; }
@media (max-width: 900px) {
  .board { grid-template-columns: 1fr; }
}
</style>
